import { portalApi } from "./client";
import type { PortalBatchResult } from "./batch";

// TPS Servicing Portal PR-E — portal-side client + DTO type for the firm's
// "Bills" review-and-approve queue. Mirrors the C# record the backend exposes
// under /api/portal/tps/bills. Kept inside the portal bundle so nothing here
// imports staff types. No React Query in the portal — callers load with
// useState and re-run the loader after approve/return.

export type TpsBillStatus =
  | "Draft"
  | "TpsApprovalPending"
  | "TpsApproved"
  | "Posted"
  | "Returned";

export interface TpsBill {
  id: string;
  loanId: string;
  loanNumber: string;
  borrowerName: string;
  /** DateOnly "YYYY-MM-DD". */
  periodStart: string;
  periodEnd: string;
  status: TpsBillStatus;
  amountDue: number;
  principal: number;
  interest: number;
  servicingFee: number;
  preparedAt: string;
  tpsApprovedAt: string | null;
  postedAt: string | null;
  returnedReason: string | null;
  /** PR-F — the borrower's ACH payment lifecycle (read-only here; the TPS
   *  does not act on payments): Initiated | Processing | Cleared | Returned
   *  | Canceled, null before any pay attempt. */
  paymentStatus: string | null;
  paidAt: string | null;
  /** Distribution Gate PR-3 — server-derived payout state, set only once the
   *  buyer payment has Cleared: "paid_out" | "returned" | "awaiting_payout"
   *  (null before clearing). Drives the "Paid — awaiting payout" pill. */
  payoutState: string | null;
  /** BLA Core §2.4 — the loan's rate of the scheduled P&I, charged on top of it. Mutually
   *  exclusive with `servicingFee`: on a loan carrying this fee Phoenix pays
   *  the servicer out of it, so `servicingFee` is 0 and the borrower sees one
   *  fee line instead of a separate servicer charge. 0 on loans without it. */
  paymentAdministrationFee: number;
}

/** WS5 — one entry of the bill's Phoenix↔servicer return conversation
 *  (chain-keyed on loan+period, so every correction round reads as one
 *  thread). */
export interface TpsBillComment {
  id: string;
  authorRole: "phoenix" | "servicer";
  authorName: string;
  body: string;
  createdAt: string;
}

export const tpsBillsApi = {
  list: () => portalApi.get<TpsBill[]>("/tps/bills"),
  get: (billId: string) => portalApi.get<TpsBill>(`/tps/bills/${billId}`),
  /** WS5 — the bill's return thread, oldest first. */
  comments: (billId: string) =>
    portalApi.get<TpsBillComment[]>(`/tps/bills/${billId}/comments`),
  /** The bill's rendered PDF (the same approved artifact) as a Blob. */
  pdf: (billId: string) => portalApi.getBlob(`/tps/bills/${billId}/pdf`),
  approve: (billId: string) =>
    portalApi.post<TpsBill>(`/tps/bills/${billId}/approve`),
  return: (billId: string, note: string) =>
    portalApi.post<TpsBill>(`/tps/bills/${billId}/return`, { note }),
  /** SP-2b — approve + post up to 50 bills in one call. Per-id results; a
   *  partial failure is reported per bill, never thrown. */
  batchApprove: (ids: string[]) =>
    portalApi.post<PortalBatchResult>("/tps/bills/batch-approve", { ids }),
};
